import { APP_NAME } from './brand';
import { LIMITS } from './constants';
import type { PublicUser } from './types';

/**
 * ClanFire Blaze, the paid plan, and Stokes, the server boosts that come with
 * it. Paddle takes the payments (and holds the prices); this is what they unlock.
 * Everything that was free before Blaze stays free.
 */
export const BLAZE_NAME = `${APP_NAME} Blaze`;

/** Stokes that come with Blaze, to put on any servers you're in. */
export const STOKES_PER_BLAZE = 2;

/** Uploads with Blaze (Cloudflare caps a request at 100 MB, and a message can carry several files). */
export const BLAZE_UPLOAD_BYTES = 50 * 1024 * 1024;

/** Free Blaze for Founders, once it goes on sale. */
export const FOUNDER_FREE_DAYS = 90;

/** Days of Blaze a gift of this many months gives. */
export const giftDays = (months: number) => (months >= 12 ? 365 : months * 30);

export type BlazePlanId = 'monthly' | 'yearly';
export type BlazeGiftId = 'gift_month' | 'gift_year';
export type BlazeCheckoutKind = BlazePlanId | BlazeGiftId;

export const GIFT_MONTHS: Record<BlazeGiftId, number> = { gift_month: 1, gift_year: 12 };

/** How long a gift lasts, in words: "1 month", "3 months", "1 year". */
export const giftLength = (months: number) => (months === 12 ? '1 year' : `${months} month${months === 1 ? '' : 's'}`);

/** A server's soundboard slots without Stokes. */
export const BASE_SOUND_SLOTS = 24;

/** What every server has, before Stokes. */
export const BASE_SERVER_PERKS = { level: 0, name: null, stokes: 0, emojiSlots: LIMITS.maxEmojisPerServer, soundSlots: BASE_SOUND_SLOTS, uploadBytes: 0, banner: false } as const;

/** Server levels: each needs that many Stokes and unlocks everything below it too. */
export const STOKE_LEVELS = [
  { level: 1, name: 'Spark', stokes: 2, emojiSlots: 100, soundSlots: 36, uploadBytes: 0, banner: true },
  { level: 2, name: 'Flame', stokes: 6, emojiSlots: 150, soundSlots: 48, uploadBytes: 25 * 1024 * 1024, banner: true },
  { level: 3, name: 'Bonfire', stokes: 12, emojiSlots: 250, soundSlots: 60, uploadBytes: 50 * 1024 * 1024, banner: true },
] as const;

export type StokeLevel = 0 | 1 | 2 | 3;
export type ServerPerks = { level: StokeLevel; name: string | null; stokes: number; emojiSlots: number; soundSlots: number; uploadBytes: number; banner: boolean };

export function serverPerks(stokeCount: number): ServerPerks {
  let perks: ServerPerks = { ...BASE_SERVER_PERKS };
  for (const level of STOKE_LEVELS) if (stokeCount >= level.stokes) perks = { ...level };
  return perks;
}

/** The largest file someone can upload: the app's own limit, raised by Blaze or by the server's level. */
export function uploadLimitBytes(base: number, opts: { blaze: boolean; stokeCount?: number }): number {
  return Math.max(base, opts.blaze ? BLAZE_UPLOAD_BYTES : 0, serverPerks(opts.stokeCount ?? 0).uploadBytes);
}

/** The Blaze badge grows the longer you've had Blaze without a break. */
export const BLAZE_TENURE = [
  { months: 0, label: 'New Blaze' },
  { months: 1, label: '1 month of Blaze' },
  { months: 3, label: '3 months of Blaze' },
  { months: 6, label: '6 months of Blaze' },
  { months: 12, label: '1 year of Blaze' },
  { months: 24, label: '2 years of Blaze' },
] as const;

/** Whole months between `since` and now (a month here is 30 days, so it never runs ahead). */
export function blazeMonths(since: string | Date, now = Date.now()): number {
  return Math.max(0, Math.floor((now - new Date(since).getTime()) / (30 * 24 * 60 * 60_000)));
}

/** The badge step for someone who's had Blaze since then: 0 (new) to 5 (two years). */
export function blazeTenureTier(since: string | Date, now = Date.now()): number {
  const months = blazeMonths(since, now);
  let tier = 0;
  BLAZE_TENURE.forEach((t, i) => {
    if (months >= t.months) tier = i;
  });
  return tier;
}

/* ------------------------------------------------------------------ */
/* What the app and the server send each other                         */
/* ------------------------------------------------------------------ */

export interface BlazeSubscriptionDTO {
  plan: 'MONTHLY' | 'YEARLY';
  /** Paddle's status: active, trialing, past_due, paused or canceled. */
  status: string;
  /** When it renews (null once it's been cancelled). */
  renewsAt: string | null;
  /** When it stops, if it's been cancelled. */
  endsAt: string | null;
}

export interface StokeDTO {
  id: string;
  serverId: string;
  serverName: string;
  serverIconUrl: string | null;
  createdAt: string;
}

/** A gift you bought. */
export interface BlazeGiftDTO {
  code: string;
  months: number;
  createdAt: string;
  redeemed: boolean;
  /** Refunded, so it can't be used. */
  revoked: boolean;
}

/** A gift code, as whoever opens the link sees it. */
export interface BlazeGiftPreviewDTO {
  months: number;
  from: PublicUser | null;
  /** Not yet redeemed (or refunded). */
  redeemable: boolean;
}

/** What the Blaze page needs to take a payment through Paddle (absent while Blaze isn't on sale). */
export interface BlazeCheckoutConfigDTO {
  environment: 'sandbox' | 'production';
  /** Paddle's client-side token: safe to show, it can only open checkouts. */
  clientToken: string;
  /** Paddle price ids, to show local prices; null where that option isn't set up. */
  prices: Record<BlazeCheckoutKind, string | null>;
}

export interface BlazeStatusDTO {
  /** Blaze is on until then; null when it's off. */
  until: string | null;
  /** When the current unbroken stretch began. */
  since: string | null;
  /** The paid subscription, if there is one that hasn't ended. */
  subscription: BlazeSubscriptionDTO | null;
  /** Where free time (Founder months, gifts, from staff) runs until, if any is left. */
  freeUntil: string | null;
  stokes: StokeDTO[];
  /** How many Stokes come with your Blaze right now. */
  stokesTotal: number;
  founder: { isFounder: boolean; claimedAt: string | null; canClaim: boolean };
  gifts: BlazeGiftDTO[];
  onSale: boolean;
  checkout: BlazeCheckoutConfigDTO | null;
}

/** Staff: whether Blaze is on sale, and whether Paddle is set up. */
export interface BlazeSaleDTO {
  onSale: boolean;
  configured: boolean;
  environment: 'sandbox' | 'production';
  giftsConfigured: boolean;
}
